import { Fragment, useState } from 'react';
import { toast } from 'sonner';
import { gql } from '@apollo/client';
import { useFragment, useMutation } from '@apollo/client/react';
import { FileSearch } from 'lucide-react';

import { apolloClient as client } from 'src/graphql';
import { SuggestedChangesView } from '@/components/SuggestChanges';
import { Button } from '@/components/ui/button';
import { ClientError, getApiError } from 'src/utils/errors';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/dialog';
import { SuggestedChangeVerdictEnum } from 'src/graphql/schema';
import { SuggestedChangeReviewDocument } from 'src/graphql/actions/suggestChanges/suggestedChangeReview.mutation';
import { SuggestedChangesDocument } from 'src/graphql/actions/suggestChanges/suggestedChanges.query';
import { getAgreementSuggestedChangesItems, type IAgreementField } from 'src/containers/SoftwareEscrow/RequestChanges';
import { suggestChangesVar, writeRejectSuggestChanges } from 'src/graphql/client/cache';

type Props = {
  suggestedChangeId: string;
};

const SuggestedChangeFragment = gql`
  fragment SuggestedChangeFragment on SuggestChange {
    id
    payload
  }
`;

const refetchSuggestedChanges = () => {
  client.cache.evict({ fieldName: 'suggestedChanges' });
  client.cache.gc();
  client.refetchQueries({
    include: [SuggestedChangesDocument],
  });
};

const ReviewAction = ({
  suggestedChangeId,
  verdict,
  successCallback,
}: {
  suggestedChangeId: string;
  verdict: SuggestedChangeVerdictEnum;
  successCallback: () => void;
}) => {
  const [reviewChanges, { loading: isLoading }] = useMutation(SuggestedChangeReviewDocument);

  const onActionProceed = async () => {
    try {
      const { data } = await reviewChanges({
        variables: {
          suggestedChangeId,
          verdict,
        },
      });

      if (data?.suggestedChangeReview?.success) {
        successCallback();
      } else {
        throw new ClientError(data?.suggestedChangeReview?.errors?.[0]);
      }
    } catch (e: unknown) {
      getApiError(e, toast.error);
    }
  };

  return (
    <Button
      disabled={isLoading}
      variant={verdict === 'accepted' ? 'default' : 'destructive'}
      isLoading={isLoading}
      onClick={onActionProceed}
    >
      {verdict === 'accepted' ? 'Accept and send for review' : 'Reject all changes'}
    </Button>
  );
};

const ReviewChangesDialog = ({ suggestedChangeId }: Props) => {
  const { refetch } = useSoftwareEscrow();
  const [isSubmitChangesDialogOpen, setIsSubmitChangesDialogOpen] = useState(false);
  const [confirmationDialog, setConfirmationDialogOpen] = useState<{
    isOpen: boolean;
    verdict: SuggestedChangeVerdictEnum | null;
  }>({
    isOpen: false,
    verdict: null,
  });
  const { data: suggestedChange } = useFragment<{
    id: string;
    payload: {
      legal_context: string;
      legal_context_other: string;
      conditions: string;
      conditions_other: string;
      jurisdiction: string;
      liability: string;
      liability_other: string;
    };
  }>({
    fragment: SuggestedChangeFragment,
    from: {
      __typename: 'SuggestChange',
      id: suggestedChangeId,
    },
  });

  if (!suggestedChange) return null;

  const fields = [
    { key: 'context', value: suggestedChange?.payload?.legal_context },
    { key: 'otherContext', value: suggestedChange?.payload?.legal_context_other },
    { key: 'conditions', value: suggestedChange?.payload?.conditions },
    { key: 'conditionsOther', value: suggestedChange?.payload?.conditions_other },
    { key: 'jurisdiction', value: suggestedChange?.payload?.jurisdiction },
    { key: 'liability', value: suggestedChange?.payload?.liability },
  ];

  const suggestedChangesItems = fields
    .filter((field) => field.value)
    .map((field) => getAgreementSuggestedChangesItems(field.key as IAgreementField, field.value as string | string[]));

  const onConfirmationDialogOpenChange = (isOpen: boolean) => {
    if (isOpen) return;

    setConfirmationDialogOpen({
      isOpen: false,
      verdict: null,
    });
    refetchSuggestedChanges();
    refetch();
  };

  const successCallback = (verdict: SuggestedChangeVerdictEnum) => () => {
    setIsSubmitChangesDialogOpen(false);
    setConfirmationDialogOpen({
      isOpen: true,
      verdict,
    });
    if (verdict === SuggestedChangeVerdictEnum.Rejected) writeRejectSuggestChanges(suggestChangesVar)('agreement');
  };

  return (
    <Fragment>
      <Dialog open={isSubmitChangesDialogOpen} onOpenChange={setIsSubmitChangesDialogOpen}>
        <DialogTrigger asChild>
          <Button size="sm">
            <FileSearch />
            Review requests
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[512px]">
          <DialogHeader>
            <DialogTitle>Do you want to apply these changes?</DialogTitle>
            <DialogDescription>
              Your counterpart has suggested these changes to your escrow. Signed agreements may have to be legally
              amended in order for us to make these changes.
              <br />
              <br />
              If you choose to reject these changes, they will be discarded and we&apos;ll let the requesting party
              know.
            </DialogDescription>
          </DialogHeader>
          <SuggestedChangesView defaultOpen items={suggestedChangesItems} />
          <DialogFooter className="sm:justify-between">
            <ReviewAction
              suggestedChangeId={suggestedChangeId}
              verdict={SuggestedChangeVerdictEnum.Rejected}
              successCallback={successCallback(SuggestedChangeVerdictEnum.Rejected)}
            />
            <ReviewAction
              suggestedChangeId={suggestedChangeId}
              verdict={SuggestedChangeVerdictEnum.Accepted}
              successCallback={successCallback(SuggestedChangeVerdictEnum.Accepted)}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmationDialog.isOpen} onOpenChange={onConfirmationDialogOpenChange}>
        <DialogContent className="sm:max-w-[441px]">
          <DialogHeader className="gap-1.5 text-center">
            <DialogTitle>Suggested changes {confirmationDialog.verdict}!</DialogTitle>
            <DialogDescription>
              {confirmationDialog.verdict === 'accepted'
                ? 'We will notify you of the outcome of our review, and as soon as these changes have been applied.'
                : 'We will notify the suggesting party. The escrow will return to its original state. '}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button className="w-full">Back to escrow</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Fragment>
  );
};

export default ReviewChangesDialog;
